import { MagicRulesFileId, MonacoLanguageType } from "@hex/common";
import { Static, String } from "runtypes";
import { EmptyObject, ValueOf } from "type-fest";

import { UseAsyncState } from "../../../hooks/useAsync";

type AnyObject = Record<string, unknown>;

export type ContextWorkbenchFile = ContextWorkbenchFile.Rule;

export const ContextWorkbenchFileId = String.withBrand(
  "ContextWorkbenchFileId",
);
export type ContextWorkbenchFileId = Static<typeof ContextWorkbenchFileId>;

export type ContextWorkbenchFileData = NonNullable<
  ContextWorkbenchFile["data"]
>;

/**
 * A file in the context workbench. A file is a pointer to an entity (or
 * entities) with some text contents. A file has no inherent functionality,
 * instead it's referenced by other models (like EditorTab)
 */
export namespace ContextWorkbenchFile {
  export const Type = {
    Rule: "rule",
  } as const;

  export type Type = ValueOf<typeof Type>;

  /**
   * A generic base type for all context workbench files. A unique type key
   * must be provided. Optionally specify properties.
   *
   * An `id` and `type` are required and specified at the top-level, this
   * ensures that these values are always present and used for intersections
   * with other workbench state like tabs.
   *
   * The additional properties are specified per-type, and can follow an async
   * pattern if the data is fetched.
   */
  type _Base<
    TType extends ContextWorkbenchFile.Type,
    TProperties extends AnyObject = EmptyObject,
  > = {
    id: ContextWorkbenchFileId;
    type: TType;
  } & TProperties;

  export type Rule = _Base<
    "rule",
    UseAsyncState<{
      magicRulesFileId: MagicRulesFileId;
      filepath: string;
      stem: string;
      extension: string;
      language: MonacoLanguageType;
      contents: string | null;
      sizeBytes: number | null;
    }>
  >;
}
